/**
 * AmbientWobble — bounded sin/cos drift applied additively at projection time.
 *
 * Goal: bring the canvas to life with a continuous gentle drift on every
 * node, **without** ever causing nodes to overlap or invalidate the static
 * radial layout that drives collision detection.
 *
 * Design rules:
 *
 *   1. The wobble is purely additive at *projection* time (i.e. the rAF tick
 *      in `useRadialMagneticLayout`). It is **never** folded into the
 *      `base` rest position, the spring `disp/target`, the layout engine's
 *      collision math, or any of the placement helpers. As far as the rest
 *      of the system is concerned, every node continues to have a single
 *      static position.
 *
 *   2. Each node's drift is bounded inside a fraction of the collision
 *      buffer that `RadialLayoutEngine` already inflates each AABB by
 *      (`COLLISION_PADDING_X = 24`, `COLLISION_PADDING_Y = 18`). With the
 *      wobble amplitude capped to half the padding, even the worst-case
 *      anti-phase pair of neighbouring nodes drifts a combined
 *      `2 * amplitude < 2 * padding` — i.e. strictly less than the buffer
 *      that already separates their AABBs at rest. They cannot overlap
 *      and cannot meaningfully clutter into each other.
 *
 *   3. When a node is being dragged, its wobble is suppressed so the
 *      pointer feels exact (matching the prototype's behaviour).
 *
 * The phase per node is derived from a stable hash of its id, so wobble
 * is reproducible across renders and de-correlated across nodes (no
 * "marching" effect where everything drifts in unison).
 */

import { COLLISION_PADDING_X, COLLISION_PADDING_Y } from './RadialLayoutEngine';

// ============================================================================
// Tuning constants
// ============================================================================

/**
 * Fraction of the rest collision padding that each node may drift inside.
 *
 * Two adjacent nodes are placed by `RadialLayoutEngine` so their inflated
 * AABBs (each inflated by `COLLISION_PADDING_*`) do not overlap — i.e.
 * the raw edge-to-edge gap between them is at least `2 * COLLISION_PADDING_*`.
 *
 * Capping each node's drift to `0.5 * COLLISION_PADDING_*` means the worst
 * possible relative drift (two neighbours wobbling exactly opposite each
 * other) is `2 * 0.5 * COLLISION_PADDING_* = COLLISION_PADDING_*` — strictly
 * less than the rest gap, so AABBs never overlap.
 */
export const WOBBLE_BUFFER_FRACTION = 0.5;

/** Maximum horizontal drift, in graph-space pixels. */
export const WOBBLE_AMPLITUDE_X: number = COLLISION_PADDING_X * WOBBLE_BUFFER_FRACTION;
/** Maximum vertical drift, in graph-space pixels. */
export const WOBBLE_AMPLITUDE_Y: number = COLLISION_PADDING_Y * WOBBLE_BUFFER_FRACTION;

/**
 * Angular speed of the underlying sin wave, in radians/second.
 * A value of ~0.6 gives a full oscillation roughly every ~10s, which
 * reads as a calm "alive" drift rather than visible motion.
 */
export const WOBBLE_SPEED = 0.6;

/**
 * Per-node phase offsets are advanced by an irrational step so adjacent
 * indices land on visibly different phases (avoids in-sync drift).
 * Same constants the prototype uses.
 */
const PHASE_X_STEP = 1.61803398875; // golden ratio
const PHASE_Y_STEP = 2.39996322973; // golden angle (rad)

// ============================================================================
// Helpers
// ============================================================================

/**
 * Stable, fast 32-bit string hash (FNV-1a variant). Used to give every
 * node a deterministic phase index that doesn't shift between renders.
 */
function hashId(id: string): number {
	let h = 2166136261 >>> 0;
	for (let i = 0; i < id.length; i++) {
		h ^= id.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return h >>> 0;
}

/**
 * Map a node id to a stable per-node phase index. The integer returned is
 * deliberately unbounded — `computeAmbient` multiplies it by the irrational
 * `PHASE_*_STEP`, so very large indices are fine and de-correlate well.
 */
export function phaseIndexForId(id: string): number {
	// Modulo a large prime to keep the float math comfortable but still
	// give us tens of thousands of distinct phases.
	return hashId(id) % 100003;
}

// ============================================================================
// Public API
// ============================================================================

export interface AmbientOffset {
	x: number;
	y: number;
}

const ZERO: AmbientOffset = { x: 0, y: 0 };

/**
 * Compute the per-node ambient drift at time `timeSec`.
 *
 * Returns `{0,0}` when wobble is suppressed (drag, reduced motion, or
 * an explicit `enabled=false`). Otherwise returns a drift vector whose
 * components are bounded by `±WOBBLE_AMPLITUDE_X` / `±WOBBLE_AMPLITUDE_Y`.
 */
export function computeAmbient(
	timeSec: number,
	phaseIndex: number,
	options: {
		enabled?: boolean;
		isDragged?: boolean;
		amplitudeScale?: number;
	} = {},
): AmbientOffset {
	const { enabled = true, isDragged = false, amplitudeScale = 1 } = options;
	if (!enabled || isDragged || amplitudeScale <= 0) return ZERO;

	const ax = WOBBLE_AMPLITUDE_X * amplitudeScale;
	const ay = WOBBLE_AMPLITUDE_Y * amplitudeScale;

	// X drives faster than Y (1.0 vs 0.7) so the resulting Lissajous
	// figure is not a tilted ellipse — it produces a soft, organic loop.
	const x = ax * Math.sin(timeSec * WOBBLE_SPEED + phaseIndex * PHASE_X_STEP);
	const y = ay * Math.sin(timeSec * WOBBLE_SPEED * 0.7 + phaseIndex * PHASE_Y_STEP);

	return { x, y };
}

/**
 * `prefers-reduced-motion` check. Returns `false` (i.e. wobble allowed)
 * when called outside a browser environment so SSR / tests don't crash.
 */
export function prefersReducedMotion(): boolean {
	if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
		return false;
	}
	try {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	} catch {
		return false;
	}
}
